"use client";
import React, { useEffect, useState, useRef } from 'react';
import { useAuth } from '../hooks/useAuth';
import supabaseService from '../services/supabaseService';
import CallScreen from './CallScreen';
import { FiVideo, FiPhoneOff, FiPhoneCall } from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';

/**
 * IncomingCallManager component:
 * Listens globally for incoming call broadcast events for the current logged-in user.
 * Displays a popup modal on any page enabling Accept or Decline.
 */
const IncomingCallManager = () => {
  const { user } = useAuth();
  const [incomingCall, setIncomingCall] = useState(null); // { conversationId, callerId, callerName }
  const [activeCall, setActiveCall] = useState(null); // { conversationId, callerId, callerName, isCaller }

  const channelRef = useRef(null);
  const audioRef = useRef(null);

  useEffect(() => {
    if (!user || !user.id) return;

    // Listen on user specific broadcast channel for call signals
    const channel = supabaseService.client.channel(`user_calls_${user.id}`);
    channelRef.current = channel;

    channel
      .on('broadcast', { event: 'incoming_call' }, (payload) => {
        console.log('📞 Global Incoming Call:', payload);
        if (payload && payload.payload) {
          const callData = payload.payload;
          setIncomingCall(callData);
          
          // Play ringtone if possible
          try {
            if (!audioRef.current) {
              audioRef.current = new Audio('https://assets.mixkit.co/active_storage/sfx/1359/1359-preview.mp3');
              audioRef.current.loop = true;
            }
            audioRef.current.play().catch((e) => console.log('Audio autoplay prevented:', e));
          } catch (e) {
            console.error('Ring audio error:', e);
          }
        }
      })
      .on('broadcast', { event: 'call_cancelled' }, () => {
        console.log('📞 Call Cancelled by caller');
        stopRingtone();
        setIncomingCall(null);
      })
      .subscribe();

    return () => {
      stopRingtone();
      if (channelRef.current) {
        supabaseService.client.removeChannel(channelRef.current);
      }
    };
  }, [user]);

  const stopRingtone = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
  };

  const handleAccept = () => {
    stopRingtone();
    if (incomingCall) {
      setActiveCall({
        conversationId: incomingCall.conversationId,
        otherUserId: incomingCall.callerId,
        otherUserName: incomingCall.callerName || 'المستخدم',
        isCaller: false
      });
      setIncomingCall(null);
    }
  };

  const handleDecline = () => {
    stopRingtone();
    if (incomingCall && incomingCall.callerId) {
      // Notify caller that call was declined
      const callerChannel = supabaseService.client.channel(`user_calls_${incomingCall.callerId}`);
      callerChannel.subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          callerChannel.send({
            type: 'broadcast',
            event: 'call_declined',
            payload: { recipientId: user.id }
          });
          setTimeout(() => {
            supabaseService.client.removeChannel(callerChannel);
          }, 1000);
        }
      });
    }
    setIncomingCall(null);
  };

  // Function exposed globally or triggered when user initiates call from ChatScreen
  const startCallGlobal = (conversationId, otherUserId, otherUserName) => {
    if (!user) return;

    // Send incoming_call signal to recipient
    const recipientChannel = supabaseService.client.channel(`user_calls_${otherUserId}`);
    recipientChannel.subscribe((status) => {
      if (status === 'SUBSCRIBED') {
        recipientChannel.send({
          type: 'broadcast',
          event: 'incoming_call',
          payload: {
            conversationId,
            callerId: user.id,
            callerName: user.user_metadata?.full_name || user.email || 'مستخدم'
          }
        });
        setTimeout(() => {
          supabaseService.client.removeChannel(recipientChannel);
        }, 1000);
      }
    });

    setActiveCall({
      conversationId,
      otherUserId,
      otherUserName,
      isCaller: true
    });
  };

  // Export helper on window for easy trigger if needed
  if (typeof window !== 'undefined') {
    window.__startVideoCall = startCallGlobal;
  }

  return (
    <>
      {/* 1. Global Incoming Call Modal Popup */}
      <AnimatePresence>
        {incomingCall && !activeCall && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: -50 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: -50 }}
            className="fixed top-6 left-1/2 transform -translate-x-1/2 z-[9999] w-[90%] max-w-md bg-gray-900/95 backdrop-blur-xl border border-white/20 text-white rounded-3xl p-5 shadow-2xl flex flex-col items-center justify-between dir-rtl"
          >
            <div className="flex items-center gap-4 w-full mb-4">
              <div className="relative">
                <div className="w-14 h-14 rounded-full bg-gradient-to-tr from-blue-600 to-indigo-600 flex items-center justify-center font-bold text-xl text-white shadow-lg animate-pulse">
                  {incomingCall.callerName ? incomingCall.callerName.charAt(0).toUpperCase() : '؟'}
                </div>
                <div className="absolute -bottom-1 -right-1 bg-green-500 text-white p-1 rounded-full text-xs animate-bounce">
                  <FiVideo />
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-bold text-base text-white truncate">
                  {incomingCall.callerName || 'متصل غير معروف'}
                </h3>
                <p className="text-xs text-blue-400 font-medium animate-pulse flex items-center gap-1">
                  <FiPhoneCall className="w-3.5 h-3.5" />
                  مكالمة فيديو واردة...
                </p>
              </div>
            </div>

            <div className="flex items-center justify-between w-full gap-4 pt-2 border-t border-white/10">
              {/* Decline Button */}
              <button
                onClick={handleDecline}
                className="flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-2xl bg-red-600 hover:bg-red-700 active:scale-95 text-white font-medium text-sm transition-all duration-200 shadow-lg"
              >
                <FiPhoneOff className="w-5 h-5" />
                رفض
              </button>

              {/* Accept Button */}
              <button
                onClick={handleAccept}
                className="flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-2xl bg-green-600 hover:bg-green-700 active:scale-95 text-white font-medium text-sm transition-all duration-200 shadow-lg ring-4 ring-green-500/30 animate-pulse"
              >
                <FiVideo className="w-5 h-5" />
                قبول
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* 2. Active Call Screen Overlay */}
      {activeCall && (
        <CallScreen
          conversationId={activeCall.conversationId}
          myId={user.id}
          otherUserName={activeCall.otherUserName}
          isCaller={activeCall.isCaller}
          onEnd={() => setActiveCall(null)}
        />
      )}
    </>
  );
};

export default IncomingCallManager;
